import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitService from '../../services/unitService';
import { parseApiError } from '../../utils/errorUtils';
import './ProductCategoryDeletePage.css';
import { ArrowLeft, Trash2 } from 'lucide-react';

const UnitDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [unit, setUnit] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadUnit();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUnit = async () => {
    setLoading(true);
    setError(null);
    try {
      const unit = await unitService.getUnitById(parseInt(id || '0'));
      if (unit) {
        setUnit(unit);
      }
      setLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, t('unit.failedToLoadUnit')).message);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!unit) {
      setError(t('unit.failedToLoadUnit'));
      return;
    }

    setIsDeleting(true);
    setError(null);
    try {
      await unitService.deleteUnit(unit.unitId);

      navigate('/products/units', {
        state: { message: t('unit.deletedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('unit.failedToDeleteUnit')).message);
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/products/units/${unit?.unitId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!unit) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{t('unit.failedToLoadUnit')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={handleCancel}
            title={t('unit.title')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('unit.title')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('common.delete')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                <strong>{t('common.error')}:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label={t('common.close')}
                ></button>
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('unit.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('unit.unitName')}</label>
                  <p className="delete-info-value">{unit.unitName}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('unit.unitCode')}</label>
                  <p className="delete-info-value delete-code">{unit.unitCode}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('unit.abbreviation')}</label>
                  <p className="delete-info-value">{unit.unitShortName}</p>
                </div>

                {unit.description && (
                  <div className="delete-info-item delete-info-item-full">
                    <label className="delete-info-label">{t('unit.description')}</label>
                    <p className="delete-info-value">{unit.description}</p>
                  </div>
                )}
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={isDeleting}
                  title={t('common.cancel')}
                >
                  {t('common.cancel')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  title={t('common.delete')}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.delete')}ing...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('common.delete')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('common.information')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('common.warning')}</strong>
                    <p>{t('unit.deleteConfirmationMessage')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('unit.permanentAction')}</strong>
                    <p>{t('unit.deleteWarning')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitDeletePage;
